import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import useIsMobile from '../../hooks/useIsMobile';
import MobileLockImage from 'images/mobile-lock.png';
import IconMC from '../../presentation/components/IconMC';
import { isZernioConnectWindow } from '../../presentation/modules/Marketing/constants/zernioConnectWindow';

const SESSION_STORAGE_KEY = 'mobile_limited_banner_seen';

/**
 * Modal component that displays a warning message to mobile users
 * about limited functionality. The modal is shown only once per session.
 * When the user closes the session and logs in again, the banner will show again.
 *
 * @component
 */
const MobileLimitedExperienceBanner = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // Use mainReducer which has the full retailer user data from AgentSerializer
  const { currentRetailerUser } = useSelector((reduxState) => reduxState.mainReducer);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show modal only if:
    // 1. User is on mobile device
    // 2. User hasn't seen the banner in this session (checked via sessionStorage)
    // 3. We have the retailer user data loaded
    // This banner shows BEFORE the MIA onboarding modal
    const hasSeenInSession = sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    
    if (
      isMobile
      && !isZernioConnectWindow()
      && currentRetailerUser
      && currentRetailerUser.id
      && !hasSeenInSession
    ) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isMobile, currentRetailerUser]);

  const handleDismiss = () => {
    // Mark as seen in sessionStorage for this session
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal
      isOpen={isVisible}
      className="mobile-limited-banner-modal"
      overlayClassName="mobile-limited-banner-overlay"
      ariaHideApp={false}
    >
      <div className="tw-wrapper">
        {/* Close button */}
        <button
          type="button"
          onClick={handleDismiss}
          className="tw-absolute tw-top-4 tw-right-4 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-1 tw-leading-none"
          aria-label="Cerrar"
        >
          <IconMC name="close-simple" size="sm" className="tw-text-[#666]" />
        </button>

        <div className="tw-flex tw-flex-col tw-items-center tw-pt-2">
          {/* Title */}
          <h2 className="tw-text-center tw-text-lg tw-font-semibold tw-text-[#333] tw-mb-2 tw-leading-snug">
            {t('mobile_banner.title')}
          </h2>

          {/* Message */}
          <p className="tw-text-center tw-text-sm tw-text-[#777] tw-leading-normal">
            {t('mobile_banner.message')}
          </p>

          {/* Mobile Lock Image */}
          <div className="tw-my-8 md:tw-my-10 lg:tw-my-12">
            <img
              src={MobileLockImage}
              alt="Usar computadora"
              className="tw-w-24 tw-h-auto"
            />
          </div>

          {/* Dismiss button */}
          <button
            type="button"
            onClick={handleDismiss}
            className="tw-bg-blue-500 tw-text-white tw-border-0 tw-rounded-full tw-py-3 tw-px-12 tw-text-base tw-font-medium tw-cursor-pointer"
            style={{ minWidth: '180px' }}
          >
            {t('mobile_banner.dismiss')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MobileLimitedExperienceBanner;
